import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  Users,
  BrainCircuit,
  Activity,
  Zap,
  ArrowRight,
  LogIn,
} from 'lucide-react';

export default function LandingPage() {
  const [showStudentJoin, setShowStudentJoin] = useState(false);
  const [roomIdInput, setRoomIdInput] = useState('');
  const navigate = useNavigate();

  const handleStudentJoin = (e: React.FormEvent) => {
    e.preventDefault();
    if (roomIdInput.trim()) {
      navigate(`/student?roomId=${encodeURIComponent(roomIdInput.trim())}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="text-white" size={18} fill="currentColor" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">EduPulse AI</h1>
          </div>
          <nav className="flex space-x-6">
            <a
              href="/teacher"
              className="text-slate-600 hover:text-indigo-600 transition"
            >
              Teacher
            </a>
            <button
              onClick={() => setShowStudentJoin(true)}
              className="text-slate-600 hover:text-emerald-600 transition"
            >
              Student
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">
            Real-Time Classroom Engagement
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            EduPulse AI connects teachers and students through privacy-first
            facial engagement analysis and AI-powered micro-polls — 100% free
            and open-source.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 w-full max-w-2xl mx-auto">
          <a href="/teacher" className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-3xl blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200" />
            <div className="relative bg-white border-2 border-slate-100 p-8 rounded-3xl flex flex-col items-center text-center transition-all hover:border-transparent">
              <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mb-6 text-indigo-600 group-hover:scale-110 transition-transform">
                <GraduationCap size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-2">I'm a Teacher</h3>
              <p className="text-slate-500 text-sm mb-4">
                Start a class and share your room code with students.
              </p>
              <span className="inline-flex items-center gap-1 text-indigo-600 font-medium text-sm">
                Start Class <ArrowRight size={16} />
              </span>
            </div>
          </a>

          <button
            onClick={() => setShowStudentJoin(true)}
            className="group relative text-left"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200" />
            <div className="relative bg-white border-2 border-slate-100 p-8 rounded-3xl flex flex-col items-center text-center transition-all hover:border-transparent">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6 text-emerald-600 group-hover:scale-110 transition-transform">
                <Users size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-2">I'm a Student</h3>
              <p className="text-slate-500 text-sm mb-4">
                Join a class using your teacher's room code.
              </p>
              <span className="inline-flex items-center gap-1 text-emerald-600 font-medium text-sm">
                Join Class <LogIn size={16} />
              </span>
            </div>
          </button>
        </div>

        {showStudentJoin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md mx-4">
              <h3 className="text-xl font-bold text-slate-800 mb-2">
                Join a Class
              </h3>
              <p className="text-slate-500 text-sm mb-6">
                Enter the room code shared by your teacher.
              </p>
              <form onSubmit={handleStudentJoin}>
                <input
                  type="text"
                  value={roomIdInput}
                  onChange={(e) => setRoomIdInput(e.target.value)}
                  placeholder="e.g. CS101 Lecture"
                  autoFocus
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-center text-2xl font-mono font-bold tracking-[0.3em] text-slate-800 placeholder:text-slate-300 placeholder:tracking-normal placeholder:font-normal focus:outline-none focus:border-emerald-500 transition"
                />
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowStudentJoin(false);
                      setRoomIdInput('');
                    }}
                    className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!roomIdInput.trim()}
                    className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Join
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="mt-16 text-center">
          <h3 className="text-2xl font-semibold text-slate-800 mb-6">
            How It Works
          </h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BrainCircuit size={32} className="text-indigo-600" />
              </div>
              <h4 className="text-lg font-medium text-slate-800 mb-2">
                AI-Powered Quizzes
              </h4>
              <p className="text-slate-600">
                Groq + Llama 3 analyzes your lecture transcript and
                auto-generates comprehension polls.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Activity size={32} className="text-emerald-600" />
              </div>
              <h4 className="text-lg font-medium text-slate-800 mb-2">
                Real-Time Engagement
              </h4>
              <p className="text-slate-600">
                Monitor student participation via facial expression analysis —
                processed entirely on-device.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-violet-600" />
              </div>
              <h4 className="text-lg font-medium text-slate-800 mb-2">
                Privacy First
              </h4>
              <p className="text-slate-600">
                No video ever leaves the student's browser. Only a single
                numeric score is transmitted.
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-200">
        <div className="max-w-7xl mx-auto text-center text-slate-600">
          <p>&copy; 2026 EduPulse AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
